import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { WorkOrdersProvider, useWorkOrderData } from '../../../../context/WorkOrdersContext';
import { useWorkOrderSettings } from '../../../../hooks/useWorkOrderSettings';
import { AppRoutes } from '../../../../routes/routeConstants/appRoutes';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { DEFAULT_SORT, NO_FILTERS } from '../../../InfrastructureManager/WorkOrders/constants';
import { groupByStatus } from '../../../InfrastructureManager/WorkOrders/workOrderList.utils';
import StatusColumn from '../../../InfrastructureManager/WorkOrders/WorkOrderStatusList/StatusColumn';
import WorkOrderCalendar from '../../../InfrastructureManager/WorkOrders/WorkOrderCalendar';
import styles from './infrastructureSummary.module.scss';

interface InfrastructureSummaryProps {
  showWorkOrderSummary?: boolean;
  showWorkOrderCalendar?: boolean;
}

// A column's width before the widget's scale-down; fits a title like "Scheduled".
const BOARD_COLUMN_REM = 12;

// The work orders page's own components shown read-only: these handlers do nothing.
const noop = () => {};

const WorkOrderSummaryBoard = () => {
  const { orders, isLoading: isOrdersLoading } = useWorkOrderData();
  const { settings, visibleStatuses, isLoading: isSettingsLoading } = useWorkOrderSettings();
  const grouped = useMemo(() => groupByStatus(orders), [orders]);
  const count = visibleStatuses.length;
  // The card prefers room for every column but may shrink; the columns then share the width it has.
  const cardVars = useMemo(() => ({ '--board-columns': count, '--board-column': `${BOARD_COLUMN_REM}rem` }) as React.CSSProperties, [count]);
  const columnVars = useMemo(
    () =>
      Object.fromEntries(
        visibleStatuses.map((status) => [`--wo-col-${status}`, `calc((100% - var(--board-gap) * ${count - 1}) / ${count})`]),
      ) as React.CSSProperties,
    [visibleStatuses, count],
  );
  // Hidden, not unmounted, while loading: the card keeps its place and no "No work orders" flashes.
  const isLoading = isOrdersLoading || isSettingsLoading;

  return (
    <div className={`${styles.card} ${styles.boardCard} ${isLoading ? styles.cardLoading : ''}`} style={cardVars}>
      <div className={styles.board} style={columnVars} role="group" aria-label="Work orders by status" aria-busy={isLoading}>
        {visibleStatuses.map((status) => (
          <StatusColumn
            key={status}
            status={status}
            orders={grouped[status]}
            sort={DEFAULT_SORT}
            color={settings.statuses[status].color}
            selectedId={null}
            canDrag={false}
            onSortChange={noop}
            onSelect={noop}
            onExpand={noop}
            onDrop={noop}
          />
        ))}
      </div>
    </div>
  );
};

const InfrastructureSummary: React.FC<InfrastructureSummaryProps> = ({ showWorkOrderSummary = true, showWorkOrderCalendar = true }) => {
  if (!showWorkOrderSummary && !showWorkOrderCalendar) return null;

  return (
    <div className={styles.summary}>
      <h3 className={styles.title}>
        <Link className={styles.titleLink} to={AppRoutes.WORK_ORDERS}>
          Infrastructure Manager
        </Link>
      </h3>

      {/* Mounted only while a card shows, so nothing is fetched when both are switched off. */}
      <WorkOrdersProvider>
        <div className={styles.cards}>
          <ConditionalRender visible={showWorkOrderSummary}>
            <WorkOrderSummaryBoard />
          </ConditionalRender>

          <ConditionalRender visible={showWorkOrderCalendar}>
            <div className={`${styles.card} ${styles.calendarCard}`}>
              <div className={styles.calendar}>
                <WorkOrderCalendar searchTerm="" filters={NO_FILTERS} />
              </div>
            </div>
          </ConditionalRender>
        </div>
      </WorkOrdersProvider>
    </div>
  );
};

export default memo(InfrastructureSummary);
